// src/web/features/materials/pages/admin/UploadMaterialPage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardTitle, CardContent, CardDescription } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Alert, AlertTitle, AlertDescription } from '@voilajsx/uikit/alert';
import { ValidatedInput } from '@voilajsx/uikit/form';
import {
  Upload,
  FileText,
  AlertCircle,
  CheckCircle,
  Shield,
  Clock,
  Lock,
  Unlock
} from 'lucide-react';
import { SEO } from '../../../../shared/components';
import { config } from '../../../auth/config';

// Predefined options (matching API)
const CLASS_SECTIONS = [
  'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6',
  'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'
];

const SUBJECTS = [
  'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'Hindi',
  'Social Science', 'Computer Science', 'Accountancy', 'Business Studies',
  'Economics', 'Geography', 'History', 'Political Science',
  'Environmental Science', 'General Knowledge'
];

const UploadMaterialPage: React.FC = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subject: '',
    classSection: '',
    accessType: 'free' as 'free' | 'restricted'
  });
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (value: string, id?: string) => {
    if (!id) return;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      // File size validation (50MB limit)
      if (selectedFile.size > 50 * 1024 * 1024) {
        setError('File size must be less than 50MB');
        return;
      }
      
      // File type validation
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ];
      
      if (!allowedTypes.includes(selectedFile.type)) {
        setError('Only PDF, DOC, DOCX, PPT, and PPTX files are allowed');
        return;
      }
      
      setFile(selectedFile);
      setError(null);
    }
  };

  const validate = () =>
    formData.title.trim() !== '' &&
    formData.subject.trim() !== '' &&
    formData.classSection.trim() !== '' &&
    file !== null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fill all required fields and select a file.');
      return;
    }

    setLoading(true);

    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('subject', formData.subject);
    data.append('classSection', formData.classSection);
    data.append('accessType', formData.accessType);
    if (file) data.append('file', file);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('User is not authenticated');

      console.log('📤 Starting secure file upload...');

      const res = await fetch(`${config.api.baseUrl}/api/materials`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: data
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || `HTTP ${res.status}: ${res.statusText}`);
      }

      const result = await res.json();
      console.log('✅ Material uploaded successfully:', result);

      setSuccess(true);
      setTimeout(() => navigate('/materials'), 2000);
    } catch (err: any) {
      console.error('❌ Upload failed:', err);
      setError(err.message || 'Failed to upload material');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Upload Material - Admin Panel"
        description="Upload new educational materials for students"
      />
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                <Upload className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-white">
                  Upload New Material
                </h1>
                <p className="mt-1 text-blue-100 text-sm">
                  Add educational content for students to access securely
                </p>
                <div className="mt-2 flex items-center gap-2 text-blue-100 text-xs">
                  <Shield className="h-3 w-3" />
                  <span>Files are stored privately and accessed via secure temporary links</span>
                </div>
              </div>
            </div>
          </div>

          {/* Upload Form Card */}
          <Card className="border-t-4 border-t-blue-600 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-blue-100 to-sky-100 px-6 py-5">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                Material Details
              </CardTitle>
              <CardDescription className="mt-1.5">
                Fill in the details and upload your educational material
              </CardDescription>
            </div>
            <CardContent className="p-6">
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {success && (
                <Alert className="mb-6 border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">Success</AlertTitle>
                  <AlertDescription className="text-green-700">
                    Material uploaded successfully and stored securely. Redirecting...
                  </AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ValidatedInput
                    id="title"
                    label="Material Title"
                    required
                    value={formData.title}
                    onChange={(value) => handleChange(value, 'title')}
                    placeholder="Enter material title"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.subject}
                      onChange={(e) => handleChange(e.target.value, 'subject')}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Subject</option>
                      {SUBJECTS.map((subject) => (
                        <option key={subject} value={subject}>
                          {subject}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Class <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.classSection}
                      onChange={(e) => handleChange(e.target.value, 'classSection')}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Class</option>
                      {CLASS_SECTIONS.map((className) => (
                        <option key={className} value={className}>
                          {className}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select File <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input 
                        type="file" 
                        accept=".pdf,.doc,.docx,.ppt,.pptx"
                        onChange={handleFileChange} 
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-1 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </div>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-500">
                        Supported formats: PDF, DOC, DOCX, PPT, PPTX (Max 50MB)
                      </p>
                      <div className="flex items-center gap-1 text-xs text-green-600">
                        <Shield className="h-3 w-3" />
                        <span>Files stored privately with secure access</span>
                      </div>
                      {file && (
                        <div className="flex items-center gap-1 text-xs text-blue-600">
                          <FileText className="h-3 w-3" />
                          <span>Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <ValidatedInput
                  id="description"
                  label="Description (Optional)"
                  value={formData.description}
                  onChange={(value) => handleChange(value, 'description')}
                  placeholder="Brief description about the material"
                />

                {/* Access Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Access Type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label
                      className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.accessType === 'free'
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="accessType"
                        value="free"
                        checked={formData.accessType === 'free'}
                        onChange={(e) => handleChange(e.target.value, 'accessType')}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Unlock className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-gray-900">Free Access</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Students can view and download this material directly
                        </p>
                      </div>
                    </label>

                    <label
                      className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.accessType === 'restricted'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="accessType"
                        value="restricted"
                        checked={formData.accessType === 'restricted'}
                        onChange={(e) => handleChange(e.target.value, 'accessType')}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Lock className="h-4 w-4 text-amber-600" />
                          <span className="font-medium text-gray-900">Restricted</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Students must contact counsellors to access this material
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Security Notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-amber-800">Security Notice</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        Files are stored privately in secure cloud storage. Students will receive temporary access links that expire after 1 hour for enhanced security.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Uploading Securely...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload Material
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate('/materials')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default UploadMaterialPage;
